/* PublicGameSummary — build 221.
   ─────────────────────────────────────────────────────────────────
   Read-only, public box-score page rendered at two routes:
     • `/p/:gameId` → parent view (no filters)
     • `/c/:gameId` → coach view (multi-select game filter at top)

   Both modes share the same render — the only difference is the
   game-filter affordance in coach mode. Pages reuse the existing
   public endpoints:
     • `GET /api/games/{game_id}/box-scores` (already public)
     • `GET /api/teams/{team_id}/games`     (already public)

   Coach mode behaviour:
     • Default selection = the linked game only.
     • Selecting additional games averages each player's raw
       counting stats across selected games (fgm/fga/3pm/3pa/
       ftm/fta/oreb/dreb/ast/stl/blk/to/pf), then recomputes
       PPI from those averaged values. Single selection = the
       raw game's stats and PPI.
     • Players who didn't appear in a selected game are simply
       omitted from that game's average (denominator = games they
       played in). */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { computePPI, formatPPI, averageRows } from '@/lib/ppi';
import { getPctColorHex } from '@/lib/statColors';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown } from 'lucide-react';

const NAVY = '#0B1824';
const ORANGE = '#ffa133';
const MUTED = '#8FA3B1';
const BORDER = '#DDE5EC';

const API = process.env.REACT_APP_BACKEND_URL;

// Public endpoints don't need an auth header, so we use a bare
// axios instance to avoid pulling in the protected api client
// (which mutates headers/retries based on the device token).
const publicApi = axios.create({ baseURL: `${API}/api` });

function derivePts(row) {
  const fgm = +row?.fgm || 0;
  const tpm = +row?.three_pm || 0;
  const ftm = +row?.ftm || 0;
  return Math.max(0, fgm - tpm) * 2 + tpm * 3 + ftm;
}

/* Percentage cell helper (build 222).
   Returns the displayed string AND the colour hex so the table cell
   can render `<td style={{ color }}>%text</td>`.
   • Zero-attempts collapses to a neutral '—' with no colour, matching
     the in-app behaviour (see /lib/statColors.js, /lib/ppi.js).
   • Otherwise the colour follows the same youth-basketball thresholds
     used in GameSummary (`getPctColorHex`) — green/yellow/red tiers
     for FG, 3PT, FT respectively. */
function pctCell(num, denom, type) {
  const d = +denom || 0;
  if (d === 0) return { text: '—', color: null };
  const value = ((+num || 0) / d) * 100;
  return {
    text: `${Math.round(value * 10) / 10}%`,
    color: getPctColorHex(value, d, type),
  };
}

/* The box-score table — identical to the one in GameSummary.js but
   read-only and stripped of edit affordances. The PPI column is shown
   only on the coach view (`showPPI=true`); the parent view omits PPI
   entirely from headers and rows. */
function BoxScoreTable({ teamName, data, showPPI }) {
  // Build 222: parent view drops the PPI column. Headers + colSpans +
  // each row's trailing cell read off this list so the markup stays
  // in sync regardless of mode.
  const headers = ['Player','PTS','FGM-A','FG%','3PM-A','3P%','FTM-A','FT%','OREB','DREB','REB','AST','STL','BLK','TO','PF', ...(showPPI ? ['PPI'] : [])];
  const colSpan = headers.length;

  return (
    <div className="rounded-xl overflow-hidden mb-6" style={{ border: `1.5px solid ${BORDER}` }}>
      <div className="px-4 py-3" style={{ background: NAVY }}>
        <h3 className="font-bold text-[18px] uppercase tracking-tight text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }} data-testid="public-team-name">{teamName}</h3>
      </div>
      {/* Build 221: mobile horizontal scroll.
          Previously `.w-full` on the <table> forced the columns to
          compress into the viewport on a phone, wrapping every cell
          and visually truncating. Switching the table to its
          intrinsic width (`max-content`) plus `whitespace-nowrap`
          on every cell makes the wrapper scroll naturally on iOS
          (with momentum scrolling via `-webkit-overflow-scrolling`).
          Player column stays sticky on the left so coaches can
          scroll the stats without losing the row label. */}
      <div className="overflow-x-auto bg-white pt-2" style={{ WebkitOverflowScrolling: 'touch' }}>
        <table className="text-[14px]" style={{ width: 'max-content', minWidth: '100%' }}>
          <thead>
            <tr style={{ background: '#FAFCFE', borderBottom: `1px solid ${BORDER}` }}>
              {headers.map((h, i) => (
                <th key={h} className={`px-2 py-2.5 ${i === 0 ? 'text-left sticky left-0 z-10' : 'text-center'} font-bold text-[12px] uppercase tracking-[.06em] whitespace-nowrap`} style={{ fontFamily: "'Barlow Condensed', sans-serif", color: MUTED, background: '#FAFCFE' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: '#F4F7FA' }}>
            {(data.players || []).length === 0 ? (
              <tr><td colSpan={colSpan} className="px-3 py-4 text-center italic" style={{ color: MUTED }}>Individual stats not tracked for this team</td></tr>
            ) : (
              data.players.map((p, i) => {
                const fg = pctCell(p.fgm, p.fga, 'fg');
                const tp = pctCell(p.three_pm, p.three_pa, '3pt');
                const ft = pctCell(p.ftm, p.fta, 'ft');
                const ppiRaw = showPPI ? computePPI(p, data.totals) : null;
                return (
                  <tr key={p.box_score_id || `${p.player_name}-${i}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium sticky left-0 bg-white whitespace-nowrap z-10">
                      <span className="text-blue-600 font-bold">#{p.jersey_number || '?'}</span> {p.player_name}
                    </td>
                    <td className="px-2 py-2 text-center font-bold whitespace-nowrap">{derivePts(p)}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{p.fgm}-{p.fga}</td>
                    <td className="px-2 py-2 text-center font-semibold whitespace-nowrap" style={fg.color ? { color: fg.color } : undefined}>{fg.text}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{p.three_pm}-{p.three_pa}</td>
                    <td className="px-2 py-2 text-center font-semibold whitespace-nowrap" style={tp.color ? { color: tp.color } : undefined}>{tp.text}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{p.ftm}-{p.fta}</td>
                    <td className="px-2 py-2 text-center font-semibold whitespace-nowrap" style={ft.color ? { color: ft.color } : undefined}>{ft.text}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{p.oreb}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{p.dreb}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{(+p.oreb || 0) + (+p.dreb || 0)}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{p.ast}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{p.stl}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{p.blk}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{p.to}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{p.pf}</td>
                    {/* PPI cell, coach view only. Negative values render
                        red so coaches can spot net-negative contributors
                        at a glance; positives keep the default navy ink. */}
                    {showPPI && (
                      <td
                        className="px-2 py-2 text-center font-bold tabular-nums whitespace-nowrap"
                        style={{ color: (ppiRaw !== null && ppiRaw < 0) ? '#dc2626' : NAVY }}
                        data-testid={`public-ppi-${p.box_score_id || p.player_name}`}
                      >
                        {formatPPI(ppiRaw)}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
            {data.totals && (() => {
              const t = data.totals;
              const fg = pctCell(t.fgm, t.fga, 'fg');
              const tp = pctCell(t.three_pm, t.three_pa, '3pt');
              const ft = pctCell(t.ftm, t.fta, 'ft');
              return (
                <tr className="bg-slate-100 font-bold border-t-2 border-slate-300">
                  <td className="px-3 py-2 sticky left-0 bg-slate-100 whitespace-nowrap z-10">TEAM TOTALS</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">{derivePts(t)}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">{t.fgm}-{t.fga}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap" style={fg.color ? { color: fg.color } : undefined}>{fg.text}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">{t.three_pm}-{t.three_pa}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap" style={tp.color ? { color: tp.color } : undefined}>{tp.text}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">{t.ftm}-{t.fta}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap" style={ft.color ? { color: ft.color } : undefined}>{ft.text}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">{t.oreb}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">{t.dreb}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">{(+t.oreb || 0) + (+t.dreb || 0)}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">{t.ast}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">{t.stl}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">{t.blk}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">{t.to}</td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">{t.pf}</td>
                  {showPPI && (<td className="px-2 py-2 text-center text-slate-400 whitespace-nowrap">—</td>)}
                </tr>
              );
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Average a side ({players, totals}) across N games into a single
   synthetic side using `averageRows` per player. Players appearing
   in only some games are averaged over the games they appeared in
   (i.e. denominator = number of games the player has a row in). */
function averageSide(sides) {
  if (!sides || sides.length === 0) return { players: [], totals: null };

  // Bucket player rows by player_id (fallback to player_name) across
  // every selected game's side. Totals get their own bucket.
  const playerBuckets = new Map();
  const totalsBucket = [];
  sides.forEach(s => {
    (s.players || []).forEach(p => {
      const key = p.player_id || p.player_name;
      if (!playerBuckets.has(key)) playerBuckets.set(key, []);
      playerBuckets.get(key).push(p);
    });
    if (s.totals) totalsBucket.push(s.totals);
  });

  const players = [];
  playerBuckets.forEach((rows) => {
    const avg = averageRows(rows);
    if (avg) players.push(avg);
  });
  // Sort by descending averaged PTS so the box score reads naturally.
  players.sort((a, b) => derivePts(b) - derivePts(a));

  const totals = totalsBucket.length > 0 ? averageRows(totalsBucket) : null;
  return { players, totals };
}

export default function PublicGameSummary({ mode = 'parent' }) {
  const { gameId } = useParams();
  const [primary, setPrimary] = useState(null);   // { game, home_team_name, away_team_name, home, away }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Coach mode only — the team's full game list + which games are
  // currently selected (defaults to just the linked game).
  const [teamGames, setTeamGames] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedBoxScores, setSelectedBoxScores] = useState({}); // { [gameId]: box-score response }

  /* Build 222: enable native page scrolling on the public report.
     ─────────────────────────────────────────────────────────────
     The in-app shell locks `body`, `html`, `#root`, `.App` to
     `overflow: hidden` + `position: fixed` (see /src/index.css)
     so the fixed top/bottom navbars don't rubber-band on iOS. The
     public `/p/` and `/c/` routes have no in-app navbars and are
     designed to open in a regular browser, so that lock leaves the
     page un-scrollable on mobile (and on the in-app Browser plugin
     handoff too). Flip the locks on mount and restore the original
     inline styles on unmount so only this page gets the override. */
  useEffect(() => {
    const targets = [
      { el: document.body,             keys: ['overflow', 'position', 'width', 'height', 'overscrollBehavior'] },
      { el: document.documentElement,  keys: ['overflow', 'height'] },
      { el: document.getElementById('root'), keys: ['overflow', 'height'] },
    ].filter(t => t.el);

    // Snapshot inline styles so we can restore them when the user
    // navigates away from the public page back into the app shell.
    const snapshot = targets.map(({ el, keys }) => ({
      el,
      saved: Object.fromEntries(keys.map(k => [k, el.style[k]])),
    }));

    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    document.body.style.width = 'auto';
    document.body.style.height = 'auto';
    document.body.style.overscrollBehavior = 'auto';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    const root = document.getElementById('root');
    if (root) {
      root.style.overflow = 'visible';
      root.style.height = 'auto';
    }

    return () => {
      snapshot.forEach(({ el, saved }) => {
        Object.entries(saved).forEach(([k, v]) => { el.style[k] = v; });
      });
    };
  }, []);

  // 1. Load the linked game's box scores.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await publicApi.get(`/games/${gameId}/box-scores`);
        if (cancelled) return;
        setPrimary(res.data);
        setSelectedIds([gameId]);
        setSelectedBoxScores({ [gameId]: res.data });
      } catch (e) {
        if (!cancelled) setError('Could not load this game.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [gameId]);

  // 2. Coach mode — load the team's other games for the dropdown.
  useEffect(() => {
    if (mode !== 'coach' || !primary?.game?.team_id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await publicApi.get(`/teams/${primary.game.team_id}/games`);
        if (!cancelled) setTeamGames(res.data || []);
      } catch {
        // Non-fatal — coach view falls back to just the linked game.
      }
    })();
    return () => { cancelled = true; };
  }, [mode, primary?.game?.team_id]);

  // 3. Coach mode — lazy-fetch box scores for any newly-selected game.
  useEffect(() => {
    if (mode !== 'coach') return;
    const missing = selectedIds.filter(id => !selectedBoxScores[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const additions = {};
      await Promise.all(missing.map(async (id) => {
        try {
          const res = await publicApi.get(`/games/${id}/box-scores`);
          additions[id] = res.data;
        } catch { /* skip games that fail */ }
      }));
      if (!cancelled) setSelectedBoxScores(prev => ({ ...prev, ...additions }));
    })();
    return () => { cancelled = true; };
  }, [mode, selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // 4. Merge box scores across selected games (build 222 fix).
  //    • 1 game selected ⇒ show that game's raw stats. The URL still
  //      points at the originally-shared game (`gameId`) but the page
  //      content is driven entirely by the dropdown selection — coach
  //      can toggle between any of the team's games without leaving
  //      the page. Score header re-renders against the active game.
  //    • Multiple selected ⇒ average each side's player rows across
  //      the selected games' box scores via `averageSide`.
  //    • Loading guard: if the selected box score hasn't been fetched
  //      yet, fall back to `primary` to keep the layout stable until
  //      the lazy fetch resolves.
  const merged = useMemo(() => {
    if (!primary) return null;
    const ids = mode === 'coach' ? selectedIds : [gameId];
    if (ids.length === 1) {
      const only = selectedBoxScores[ids[0]];
      return only || primary;
    }
    const sides = ids.map(id => selectedBoxScores[id]).filter(Boolean);
    if (sides.length <= 1) return sides[0] || primary;
    // Pick the first loaded side's `game` / team names as the rendering
    // base — the score header is hidden in averaged mode anyway, so
    // only `home_team_name` / `away_team_name` matter here.
    return {
      ...sides[0],
      home: averageSide(sides.map(s => s.home)),
      away: averageSide(sides.map(s => s.away)),
    };
  }, [primary, selectedIds, selectedBoxScores, mode, gameId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F4F7FA' }}>
        <p className="text-[14px]" style={{ color: MUTED, fontFamily: "'Barlow', sans-serif" }}>Loading game…</p>
      </div>
    );
  }
  if (error || !merged) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F4F7FA' }}>
        <p className="text-[14px]" style={{ color: MUTED, fontFamily: "'Barlow', sans-serif" }}>{error || 'Game not found.'}</p>
      </div>
    );
  }

  const { game, home_team_name, away_team_name, home, away } = merged;
  const isAveraged = mode === 'coach' && selectedIds.length > 1;
  const dateStr = game?.date_time ? new Date(game.date_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen" style={{ background: '#F4F7FA', fontFamily: "'Barlow', sans-serif" }}>
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Coach-only game-filter (build 222) — a multi-select dropdown
            replacing the wrap-around pill row. Tap the trigger to open
            a popover of checkboxes (one per team game). Default
            selection is the linked game; tapping additional rows
            averages raw counting stats across the selected games and
            re-derives PPI from those averages. Selecting a single game
            falls back to that game's raw stats and PPI. */}
        {mode === 'coach' && (
          <div className="bg-white rounded-xl p-4 mb-4" style={{ border: `1.5px solid ${BORDER}` }} data-testid="coach-game-filter">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] font-bold uppercase tracking-[.1em]" style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>Games</p>
              {teamGames.length > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedIds(teamGames.map(g => g.game_id))}
                    data-testid="coach-filter-select-all"
                    className="text-[11px] font-bold uppercase tracking-[.06em] px-2.5 py-1 rounded-full transition-all active:scale-[.97]"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif", background: 'transparent', color: NAVY, border: `1px solid ${BORDER}` }}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedIds([gameId])}
                    data-testid="coach-filter-clear"
                    className="text-[11px] font-bold uppercase tracking-[.06em] px-2.5 py-1 rounded-full transition-all active:scale-[.97]"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif", background: 'transparent', color: NAVY, border: `1px solid ${BORDER}` }}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="coach-filter-dropdown-trigger"
                  className="mt-2 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left"
                  style={{ background: '#FFFFFF', border: `1.5px solid ${BORDER}`, fontFamily: "'Barlow Condensed', sans-serif" }}
                  disabled={teamGames.length === 0}
                >
                  <span className="text-[13px] font-bold uppercase tracking-[.04em]" style={{ color: NAVY }}>
                    {(() => {
                      if (teamGames.length === 0) return 'Only this game available';
                      if (selectedIds.length === 1) {
                        const g = teamGames.find(x => x.game_id === selectedIds[0]);
                        return g
                          ? `${new Date(g.date_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} vs ${g.opponent_name || '—'}`
                          : '1 game selected';
                      }
                      return `${selectedIds.length} games selected`;
                    })()}
                  </span>
                  <ChevronDown className="w-4 h-4 shrink-0" style={{ color: MUTED }} />
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[280px] sm:w-[340px]" align="start">
                {/* Single-line multi-select list (build 222 polish).
                    Each row is a checkbox + a compact one-line label
                    so games are easy to tap on a phone. Selected rows
                    drive the merged stats / averaged PPI surfaced
                    below — flipping any checkbox immediately updates
                    the box score. Maintains the "always keep ≥1
                    selected" invariant by disabling the last
                    remaining selected row. */}
                <div className="max-h-[60vh] overflow-y-auto py-1" data-testid="coach-filter-dropdown-content">
                  {teamGames.map(g => {
                    const selected = selectedIds.includes(g.game_id);
                    const isOnly = selected && selectedIds.length === 1;
                    const toggle = () => {
                      if (isOnly) return; // can't deselect the last one
                      setSelectedIds(prev => {
                        if (prev.includes(g.game_id)) {
                          const next = prev.filter(x => x !== g.game_id);
                          return next.length === 0 ? [g.game_id] : next;
                        }
                        return [...prev, g.game_id];
                      });
                    };
                    return (
                      <button
                        key={g.game_id}
                        type="button"
                        onClick={toggle}
                        data-testid={`coach-filter-game-${g.game_id}`}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 text-left"
                        style={{ background: 'transparent', border: 'none', cursor: isOnly ? 'default' : 'pointer' }}
                      >
                        {/* Deterministic square checkbox (build 222 polish).
                            Native shadcn Checkbox + Radix can render
                            visually stretched in some mobile webviews;
                            this inline 18×18 box with an SVG check
                            guarantees a square shape and consistent
                            check appearance across iOS/Android/web. */}
                        <span
                          aria-hidden="true"
                          className="shrink-0 flex items-center justify-center"
                          style={{
                            width: 18,
                            height: 18,
                            border: `1.5px solid ${selected ? NAVY : BORDER}`,
                            background: selected ? NAVY : '#FFFFFF',
                            borderRadius: 3,
                            opacity: isOnly ? 0.85 : 1,
                          }}
                        >
                          {selected && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </span>
                        <span
                          className="flex-1 min-w-0 text-[13px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis"
                          style={{ color: NAVY, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '.02em' }}
                        >
                          {new Date(g.date_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} vs {g.opponent_name || '—'}
                          <span className="ml-1.5 text-[11px] tabular-nums" style={{ color: MUTED }}>
                            ({g.home_score ?? 0}-{g.away_score ?? 0})
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
            {isAveraged && (
              <p className="text-[11px] mt-3" style={{ color: MUTED }} data-testid="coach-averaging-note">
                Stats below are averages across the {selectedIds.length} selected games. PPI is recomputed from the averaged totals.
              </p>
            )}
          </div>
        )}

        {/* Score header. Hidden in averaged mode (scores don't average meaningfully). */}
        {!isAveraged && (
          <div className="rounded-xl overflow-hidden mb-6" style={{ background: NAVY }} data-testid="public-score-header">
            <div className="px-5 py-5">
              <div className="text-[14px] font-bold tracking-[.08em] uppercase mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#FFFFFF' }}>
                {dateStr}
              </div>
              <div className="flex items-center justify-center gap-6">
                <div className="text-center min-w-[80px]">
                  <p className="text-[14px] font-bold uppercase tracking-[.08em] mb-1.5" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#FFFFFF' }}>{home_team_name}</p>
                  <p className="text-4xl font-bold tabular-nums text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{game?.home_score ?? 0}</p>
                </div>
                <div className="text-2xl font-bold" style={{ color: 'rgba(255,255,255,.2)' }}>–</div>
                <div className="text-center min-w-[80px]">
                  <p className="text-[14px] font-bold uppercase tracking-[.08em] mb-1.5" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#FFFFFF' }}>{away_team_name}</p>
                  <p className="text-4xl font-bold tabular-nums text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{game?.away_score ?? 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <BoxScoreTable teamName={home_team_name} data={home || { players: [], totals: null }} showPPI={mode === 'coach'} />
        <BoxScoreTable teamName={away_team_name} data={away || { players: [], totals: null }} showPPI={mode === 'coach'} />

        <p className="text-center text-[11px] mt-6" style={{ color: MUTED }}>
          <span style={{ color: ORANGE, fontWeight: 800, letterSpacing: '0.08em' }}>SMARTERHOOPS</span> · {mode === 'coach' ? 'Coach View' : 'Parent View'}
        </p>
      </div>
    </div>
  );
}
